import React, { useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AuthContext } from '../../contexts/AuthContext';
import { useEnvironment } from '../../contexts/EnvironmentContext';
import studyService from '../../services/studyService';
import permissionsService from '../../services/permissionsService';
import { 
  Book, 
  Plus, 
  Loader, 
  Search, 
  ChevronRight, 
  Grid, 
  List, 
  Beaker, 
  SortAsc, 
  SortDesc, 
  Filter,
  Calendar,
  ArrowUpRight,
  ChevronLeft,
  CheckCircle,
  Clock,
  AlertCircle,
  MoveRight,
  ArrowRight
} from 'lucide-react';
import SponsorSelector from '../Sponsor/SponsorSelector';
import PageHeader from '../Shared/PageHeader';

const convertToCSV = (studies) => {
  if (!Array.isArray(studies) || studies.length === 0) return '';

  const headers = ['ID', 'Title', 'Status', 'Last Updated'];
  const rows = studies.map(study => [
    study.id,
    study.title,
    study.status.replace('_', ' '),
    study.updated_at ? new Date(study.updated_at).toLocaleDateString() : 'N/A'
  ]);

  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.join(','))
  ].join('\n');

  return csvContent;
};

const StudiesPage = () => {
  const { isAuthenticated, user } = useContext(AuthContext);
  const { isDemo } = useEnvironment();
  const navigate = useNavigate();
  const [studies, setStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('studyViewPreferences.viewMode') || 'grid';
  });
  const [sortBy, setSortBy] = useState(() => {
    return localStorage.getItem('studyViewPreferences.sortBy') || 'lastUpdated';
  });
  const [sortOrder, setSortOrder] = useState(() => {
    return localStorage.getItem('studyViewPreferences.sortOrder') || 'desc';
  });
  const [filterStatus, setFilterStatus] = useState(() => {
    return localStorage.getItem('studyViewPreferences.filterStatus') || 'all';
  });
  const [showSponsorSelector, setShowSponsorSelector] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [permissions, setPermissions] = useState({
    canAccessStudyBuilder: false
  });

  // Strict type checking for is_internal - explicitly check for false
  const isInternalUser = user?.is_internal === true;
  
  // Optional: Clear preferences on logout
  const clearPreferences = useCallback(() => {
    localStorage.removeItem('studyViewPreferences.viewMode');
    localStorage.removeItem('studyViewPreferences.sortBy');
    localStorage.removeItem('studyViewPreferences.sortOrder');
    localStorage.removeItem('studyViewPreferences.filterStatus');
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      clearPreferences();
    }
  }, [isAuthenticated, clearPreferences]);

  // Prevent non-internal users from accessing study creation
  useEffect(() => {
    if (!isInternalUser) {
      const path = window.location.pathname;
      if (path.includes('/studybuilder')) {
        navigate('/studies');
      }
    }
  }, [user, navigate, isInternalUser]);

  const fetchStudies = useCallback(async () => {
    // Backend extracts sponsor_id from JWT token automatically via get_sponsor_id_from_request()
    // No need to wait for selectedSponsor in localStorage
    
    if (isAuthenticated) {
      try {
        const response = await studyService.getStudies();
        
        // Handle both array and object responses
        // studyService now returns the results array directly
        const studiesData = Array.isArray(response) ? response : [];
        
        const filteredStudies = studiesData.filter(study => study && study.id);

        // For users with only one study, redirect directly to that study (bypass study selection)
        // Exception: Program Admins and superusers should always see the study list
        const shouldBypassStudyList = filteredStudies.length === 1 
          && user?.role !== 'Program Admin' 
          && !user?.is_superuser;
        
        if (shouldBypassStudyList) {
          console.log(`[STUDY_BYPASS] User has exactly 1 study, auto-redirecting to: ${filteredStudies[0].title} (ID: ${filteredStudies[0].id})`);
          navigate(`/studies/${filteredStudies[0].external_id}`);
          return;
        }
        
        setStudies(filteredStudies);
        setError(null);
      } catch (error) {
        console.error('Error fetching studies:', error);
        setError(error.message || 'Failed to fetch studies');
      } finally {
        setLoading(false);
      }
    }
  }, [isAuthenticated, isInternalUser, navigate, user]);

  const initialized = useRef(false);
  
  // Track sponsor context to re-fetch when it changes
  const [sponsorContext, setSponsorContext] = useState(() => {
    return localStorage.getItem('selectedSponsor');
  });

  // Derive sponsor name from the stored JSON
  const sponsorName = useMemo(() => {
    try {
      const parsed = JSON.parse(sponsorContext);
      return parsed?.name || null;
    } catch {
      return null;
    }
  }, [sponsorContext]);
  
  // Listen for localStorage changes (e.g., sponsor selection in another tab or after auto-bypass)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'selectedSponsor') {
        console.log('[STUDIES] Sponsor context changed via storage event');
        setSponsorContext(e.newValue);
        // Reset initialization to allow re-fetch with new sponsor context
        initialized.current = false;
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  useEffect(() => {
    // Update sponsor context state when component receives focus (handles same-tab updates)
    const currentSponsor = localStorage.getItem('selectedSponsor');
    if (currentSponsor !== sponsorContext) {
      console.log('[STUDIES] Sponsor context updated on effect run');
      setSponsorContext(currentSponsor);
      initialized.current = false; // Reset to allow fetch
    }
    
    if (initialized.current)
      return;
    initialized.current = true;

    const initializeStudies = async () => {
      try {
        await fetchStudies();
      } catch (error) {
        console.error('Error initializing studies:', error);
        setError(error.message || 'Failed to initialize studies');
      } finally {
        setLoading(false);
      }
    };

    initializeStudies();
  }, [user, navigate, fetchStudies, isInternalUser, sponsorContext]);

  const handleSponsorSelect = useCallback(() => {
    setShowSponsorSelector(false);
    fetchStudies();
  }, [fetchStudies]);

  // Force re-render when user changes
  useEffect(() => {
    setForceUpdate(prev => prev + 1);
  }, [user]);

  // Get permissions from AuthContext instead of making API calls
  const { userPermissions } = useContext(AuthContext);
  
  useEffect(() => {
    // Use permissions from AuthContext to avoid duplicate API calls
    setPermissions({
      canAccessStudyBuilder: userPermissions?.can_access_study_builder || false
    });
  }, [userPermissions]);

  // Save preferences whenever they change
  useEffect(() => {
    localStorage.setItem('studyViewPreferences.viewMode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    localStorage.setItem('studyViewPreferences.sortBy', sortBy);
  }, [sortBy]);

  useEffect(() => {
    localStorage.setItem('studyViewPreferences.sortOrder', sortOrder);
  }, [sortOrder]);

  useEffect(() => {
    localStorage.setItem('studyViewPreferences.filterStatus', filterStatus);
  }, [filterStatus]);

  const filteredAndSortedStudies = useMemo(() => {
    if (!Array.isArray(studies)) return [];
    
    const titleFiltered = studies.filter(study => 
      study?.title?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    const statusFiltered = titleFiltered.filter(study => 
      (filterStatus === 'all' || study?.status === filterStatus)
    );
    
    const filtered = statusFiltered.sort((a, b) => {
      let comparison = 0;
      if (sortBy === 'lastUpdated') {
        comparison = new Date(b?.updated_at || 0) - new Date(a?.updated_at || 0);
      } else if (sortBy === 'title') {
        comparison = (a?.title || '').localeCompare(b?.title || '');
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });
      
    return filtered;
  }, [studies, searchTerm, sortBy, sortOrder, filterStatus]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 10, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 30
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-emerald-500" />;
      case 'in_progress':
        return <Clock className="w-4 h-4 text-blue-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-amber-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'completed':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'in_progress':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      default:
        return 'bg-amber-50 text-amber-700 border-amber-200';
    }
  };

  const StudyCard = ({ study }) => (
    <motion.div
      variants={itemVariants}
      whileHover={{ 
        y: -4, 
        transition: { duration: 0.2 } 
      }}
      className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm dark:shadow-gray-900/20 overflow-hidden transition-all duration-300 hover:shadow-lg dark:hover:shadow-gray-900/20 border border-gray-100 dark:border-gray-700 cursor-pointer group relative flex flex-col h-full"
      onClick={() => navigate(`/studies/${study.external_id}`)}
    >
      <div className="p-6 flex-1 flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <div className="bg-primary-50 p-3 rounded-full">
            <Book className="w-5 h-5 text-primary-600" />
          </div>
          {isInternalUser && (
            <div className={`text-xs font-medium px-3 py-1.5 rounded-full border flex items-center space-x-1.5 ${getStatusColor(study.status)}`}>
              {getStatusIcon(study.status)}
              <span>{study.status.replace('_', ' ')}</span>
            </div>
          )}
        </div>
        
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2 group-hover:text-primary-600 transition-colors duration-300">
          {study.title}
        </h2>
        
        <div className="mt-auto pt-4 flex items-center text-xs text-gray-500 dark:text-gray-400">
          <Calendar className="w-3.5 h-3.5 mr-1.5" />
          <span>Last updated: {formatDate(study.updated_at)}</span>
        </div>
      </div>
      
      <div className="mt-auto px-6 py-3 bg-gray-50 dark:bg-gray-900 border-t border-gray-100 dark:border-gray-700 flex justify-between items-center">
        <span className="text-sm font-medium text-primary-600">View details</span>
        <ArrowUpRight className="w-4 h-4 text-primary-600 transform group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
      </div>
    </motion.div>
  );

  const StudyListItem = ({ study }) => (
    <motion.div
      variants={itemVariants}
      whileHover={{ x: 2, transition: { duration: 0.2 } }}
      className="bg-white dark:bg-gray-800 rounded-xl shadow-sm dark:shadow-gray-900/20 border border-gray-100 dark:border-gray-700 overflow-hidden transition-all duration-300 hover:shadow-md dark:hover:shadow-gray-900/20 cursor-pointer group"
      onClick={() => navigate(`/studies/${study.external_id}`)}
    >
      <div className="p-4 flex items-center justify-between">
        <div className="flex items-center flex-1 min-w-0">
          <div className="bg-primary-50 p-2.5 rounded-full mr-4 flex-shrink-0">
            <Book className="w-5 h-5 text-primary-600" />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 group-hover:text-primary-600 transition-colors duration-300 truncate">{study.title}</h2>
            <p className="text-gray-500 dark:text-gray-400 text-sm flex items-center">
              <Calendar className="w-3.5 h-3.5 mr-1.5 flex-shrink-0" />
              <span>Last updated: {formatDate(study.updated_at)}</span>
            </p>
          </div>
        </div>
        <div className="flex items-center ml-4">
          {isInternalUser && (
            <div className={`text-xs font-medium px-3 py-1.5 rounded-full border flex items-center space-x-1.5 mr-3 ${getStatusColor(study.status)}`}>
              {getStatusIcon(study.status)}
              <span>{study.status.replace('_', ' ')}</span>
            </div>
          )}
          <MoveRight className="w-5 h-5 text-primary-600 transform group-hover:translate-x-1 transition-transform duration-300" />
        </div>
      </div>
    </motion.div>
  );

  const EmptyStateIllustration = () => (
    <div className="relative w-40 h-40 mx-auto mb-6">
      <div className="absolute inset-0 bg-primary-100 rounded-full opacity-30"></div>
      <div className="absolute inset-4 bg-primary-200 rounded-full opacity-40"></div>
      <div className="absolute inset-10 bg-primary-300 rounded-full opacity-50"></div>
      <div className="absolute inset-0 flex items-center justify-center">
        <Beaker className="w-16 h-16 text-primary-600" />
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="relative w-16 h-16">
          <div className="absolute inset-0 rounded-full border-4 border-gray-200 dark:border-gray-700"></div>
          <div className="absolute inset-0 rounded-full border-4 border-primary-600 border-t-transparent animate-spin"></div>
        </div>
        <p className="mt-4 text-gray-600 dark:text-gray-300 font-medium">Loading your studies...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900 px-4">
        <div className="relative w-24 h-24 mb-6">
          <div className="absolute inset-0 bg-red-100 rounded-full opacity-30"></div>
          <div className="absolute inset-4 bg-red-200 rounded-full opacity-30"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <AlertCircle className="w-12 h-12 text-red-600" />
          </div>
        </div>
        
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-3 text-center">Unable to Load Studies</h2>
        <p className="text-gray-600 dark:text-gray-300 mb-6 text-center max-w-md">{error}</p>
        
        <button
          onClick={fetchStudies}
          className="px-6 py-3 bg-primary-600 text-white rounded-full hover:bg-primary-700 transition-colors shadow-sm dark:shadow-gray-900/20 font-medium flex items-center"
        >
          <Loader className="w-5 h-5 mr-2" />
          Try Again
        </button>
      </div>
    );
  }

  if (showSponsorSelector) {
    return <SponsorSelector onSponsorSelect={handleSponsorSelect} />;
  }

  if (!loading && (!studies || studies.length === 0)) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center py-16"
          >
            <EmptyStateIllustration />
            
            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">No Studies Found</h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-xl mx-auto">
              {isInternalUser
                ? "You haven't been assigned to any studies yet. Contact your administrator or create your first study to get started!"
                : "You haven't been assigned to any studies yet. Contact your study administrator for more information."}
            </p>
            
            {permissions.canAccessStudyBuilder && (
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => navigate('/studybuilder')}
                className="inline-flex items-center px-8 py-4 bg-primary-600 text-white rounded-full text-lg font-medium hover:bg-primary-700 transition-all duration-300 shadow-md dark:shadow-gray-900/20"
              >
                <Plus className="mr-2" size={20} />
                Create Your First Study
              </motion.button>
            )}
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Only internal users can reach the sponsor screen (FECOA-1784) */}
        {isInternalUser && (
          <button
            onClick={() => navigate('/sponsor-selector')}
            className="group mb-4 inline-flex items-center text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
          >
            <ChevronLeft className="w-5 h-5 mr-1 group-hover:-translate-x-1 transition-transform duration-200" />
            <span className="font-medium">Back to Sponsor</span>
          </button>
        )}
        <PageHeader
          icon={Book}
          title={sponsorName ? `${sponsorName} Study Dashboard` : 'Study Dashboard'}
          subtitle="Manage and explore your research studies"
        >

          {permissions.canAccessStudyBuilder && (
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => navigate('/studybuilder')}
              className="inline-flex items-center px-5 py-2.5 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-all duration-200 shadow-sm dark:shadow-gray-900/20"
            >
              <ArrowRight className="mr-2" size={16} />
              Study Builder
            </motion.button>
          )}
        </PageHeader>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="mb-8 rounded-2xl bg-white dark:bg-gray-800 shadow-sm dark:shadow-gray-900/20 border border-gray-100 dark:border-gray-700 p-4"
        >
          <div className="flex flex-col lg:flex-row lg:items-center space-y-4 lg:space-y-0 lg:space-x-4">
            <div className="relative flex-grow">
              <input
                type="text"
                placeholder="Search studies..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-4 focus:ring-primary-500/15 outline-none transition-all duration-200"
              />
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={18} />
            </div>
            
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-3 lg:flex-none">
              <div className="relative">
                <button
                  onClick={() => setShowDropdown(!showDropdown)}
                  className="flex items-center justify-between w-full px-4 py-2.5 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-700 dark:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600 transition-colors duration-200"
                >
                  <div className="flex items-center">
                    <Filter size={16} className="mr-2 text-gray-500 dark:text-gray-400" />
                    <span>{filterStatus === 'all' ? 'All Status' : filterStatus.replace('_', ' ')}</span>
                  </div>
                  <ChevronRight className={`w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${showDropdown ? 'rotate-90' : ''}`} />
                </button>
                
                {showDropdown && (
                  <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 rounded-xl shadow-lg dark:shadow-gray-900/20 border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="py-1">
                      {['all', 'planned', 'in_progress', 'completed'].map((status) => (
                        <button
                          key={status}
                          className={`block w-full text-left px-4 py-2 text-sm ${
                            filterStatus === status 
                              ? 'bg-primary-50 text-primary-700 font-medium' 
                              : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
                          }`}
                          onClick={() => {
                            setFilterStatus(status);
                            setShowDropdown(false);
                          }}
                        >
                          {status === 'all' ? 'All Status' : status.replace('_', ' ')}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="flex items-center space-x-3">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="pl-3 pr-8 py-2.5 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-700 dark:text-gray-200 focus:border-primary-500 focus:ring-4 focus:ring-primary-500/15 outline-none transition-all duration-200 appearance-none"
                  style={{ backgroundImage: "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e\")", backgroundPosition: "right 0.5rem center", backgroundRepeat: "no-repeat", backgroundSize: "1.5em 1.5em", paddingRight: "2.5rem" }}
                >
                  <option value="lastUpdated">Last Updated</option>
                  <option value="title">Title</option>
                </select>
                
                <button
                  onClick={() => setSortOrder(order => order === 'asc' ? 'desc' : 'asc')}
                  className="p-2.5 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-700 dark:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600 transition-colors duration-200"
                  aria-label={sortOrder === 'asc' ? "Sort descending" : "Sort ascending"}
                >
                  {sortOrder === 'asc' ? <SortAsc size={18} /> : <SortDesc size={18} />}
                </button>
                
                <div className="flex bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2.5 transition-colors duration-200 ${viewMode === 'grid' ? 'bg-primary-600 text-white' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                    aria-label="Grid view"
                  >
                    <Grid size={18} />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2.5 transition-colors duration-200 ${viewMode === 'list' ? 'bg-primary-600 text-white' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                    aria-label="List view"
                  >
                    <List size={18} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        <AnimatePresence mode="wait">
          {filteredAndSortedStudies.length === 0 ? (
            <motion.div
              key="empty-search"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.4 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm dark:shadow-gray-900/20 border border-gray-100 dark:border-gray-700 py-12 px-4 text-center"
            >
              <div className="max-w-md mx-auto">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full mb-4">
                  <Search className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">No matching studies</h3>
                <p className="text-gray-600 dark:text-gray-300 mb-6">
                  No studies match your current search criteria. Try adjusting your filters or search terms.
                </p>
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setFilterStatus('all');
                  }}
                  className="inline-flex items-center px-5 py-2.5 bg-primary-600 text-white rounded-full text-sm font-medium hover:bg-primary-700 transition-colors shadow-sm dark:shadow-gray-900/20"
                >
                  Clear filters
                </button>
              </div>
            </motion.div>
          ) : viewMode === 'grid' ? (
            <motion.div
              key="grid"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              exit={{ 
                opacity: 0,
                transition: { duration: 0.2 } 
              }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            >
              {filteredAndSortedStudies.map((study) => (
                <StudyCard key={study.id} study={study} />
              ))}
              
              {permissions.canAccessStudyBuilder && (
                <motion.div
                  variants={itemVariants}
                  whileHover={{ y: -4, transition: { duration: 0.2 } }}
                  className="bg-gradient-to-b from-primary-500 to-primary-600 rounded-2xl shadow-md dark:shadow-gray-900/20 overflow-hidden cursor-pointer group h-full"
                  onClick={() => navigate('/studybuilder/create')}
                >
                  <div className="p-6 h-full flex flex-col">
                    <div className="bg-white/20 p-3 rounded-full w-fit mb-4">
                      <Plus className="w-5 h-5 text-white" />
                    </div>
                    
                    <div className="mt-auto">
                      <h2 className="text-xl font-semibold text-white mb-2">Create New Study</h2>
                      <p className="text-primary-100 text-sm">Create a new study and configure all details</p>
                    </div>
                    
                    <div className="mt-6 flex items-center text-primary-100 text-sm">
                      <span className="font-medium mr-2">Get started</span>
                      <ArrowUpRight className="w-4 h-4 transform group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
                    </div>
                  </div>
                </motion.div>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="list"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              exit={{ 
                opacity: 0,
                transition: { duration: 0.2 } 
              }}
              className="flex flex-col space-y-3"
            >
              {filteredAndSortedStudies.map((study) => (
                <StudyListItem key={study.id} study={study} />
              ))}
              
              {permissions.canAccessStudyBuilder && (
                <motion.div
                  variants={itemVariants}
                  whileHover={{ x: 2, transition: { duration: 0.2 } }}
                  className="bg-gradient-to-r from-primary-500 to-primary-600 rounded-xl shadow-md dark:shadow-gray-900/20 overflow-hidden cursor-pointer group"
                  onClick={() => navigate('/studybuilder/create')}
                >
                  <div className="p-4 flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="bg-white/20 p-2.5 rounded-full mr-4">
                        <Plus className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h2 className="text-lg font-semibold text-white">Create New Study</h2>
                        <p className="text-primary-100 text-sm">Create a new study and configure all details</p>
                      </div>
                    </div>
                    <MoveRight className="w-5 h-5 text-white transform group-hover:translate-x-1 transition-transform duration-300" />
                  </div>
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default StudiesPage;